import i18next from 'i18next';
import { DateFormatter } from 'infrastructure/functions';
import { DateFormats } from 'infrastructure/enums';
import CopyableText from 'components/atoms/buttons/copyable-text';
import CopyIcon from 'components/atoms/icons/copyIcon';
import { tableSortMap } from 'infrastructure/antd/functions/create-sort-order';
import { useAppDispatch } from 'store';
import { Link } from 'react-router-dom';
import { Routes } from 'router/routes';

import s from './styles.module.scss';
import NoteCell from '../note-cell';
import DueDateCell from '../due-date-cell';

import type {
  INewlyTableData,
  INewlyTableDataFilters,
  IKeyNoteData,
} from 'infrastructure/interfaces';
import type { IBaseTableColumns } from 'components/atoms/base-table/types';

interface Props {
  filter: INewlyTableDataFilters;
}

export const useColumns = ({
  filter,
}: Props): IBaseTableColumns<INewlyTableData> => {
  const {
    ccmConsole: { setTableNote, toggleOpenDrawer },
  } = useAppDispatch();

  return [
    {
      label: i18next.t('labels.name'),
      key: 'name',
      width: '220px',
      dataCy: 'newly-enrolled-patients-name',
      sortable: true,
      sortKey: 'name',
      sortOrder: filter['n-name'] ? tableSortMap[filter['n-name']] : undefined,
      render(name: string, record) {
        return (
          <Link to={Routes.patientGuid(record.guid)} className={s.link}>
            {name}
          </Link>
        );
      },
    },
    {
      label: i18next.t('labels.ptID'),
      key: 'guid',
      width: '80px',
      align: 'center',
      dataCy: 'newly-enrolled-patients-guid',
      render(guid: string) {
        return (
          <CopyableText
            wrapperClassName={s.copyable}
            value={guid}
            label="Copy"
            icon={<CopyIcon />}
          />
        );
      },
    },
    {
      label: i18next.t('labels.provider'),
      key: 'provider',
      width: '190px',
      align: 'left',
      dataCy: 'newly-enrolled-patients-provider',
      sortable: true,
      sortOrder: filter['n-provider']
        ? tableSortMap[filter['n-provider']]
        : undefined,
      render(provider: string) {
        return provider;
      },
    },
    {
      label: i18next.t('labels.enrolledDate'),
      key: 'enrolledDate',
      width: '90px',
      align: 'center',
      dataCy: 'newly-enrolled-patients-enrolled-date',
      sortable: true,
      sortOrder: filter['n-enrolledDate']
        ? tableSortMap[filter['n-enrolledDate']]
        : undefined,
      render(enrolledDate: string) {
        return DateFormatter({
          date: enrolledDate,
          format: DateFormats['MMM d, yyyy'],
        });
      },
    },
    {
      label: i18next.t('labels.dueDate'),
      key: 'dueDate',
      width: '120px',
      align: 'center',
      dataCy: 'newly-enrolled-patients-due-date',
      sortable: true,
      sortOrder: filter['n-dueDate']
        ? tableSortMap[filter['n-dueDate']]
        : undefined,
      render(dueDate: string | null, record) {
        return <DueDateCell date={dueDate} patientGuid={record.guid} />;
      },
    },
    {
      label: i18next.t('labels.lastCall'),
      key: 'lastCallDate',
      width: '95px',
      align: 'center',
      dataCy: 'newly-enrolled-patients-last-call-date',
      sortable: false,
      sortOrder: filter['n-lastCallDate']
        ? tableSortMap[filter['n-lastCallDate']]
        : undefined,
      render(lastCallDate: string) {
        return DateFormatter({
          date: lastCallDate,
          format: DateFormats['MMM d, yyyy'],
        });
      },
    },
    {
      label: i18next.t('labels.ccmAppt'),
      key: 'appointmentDate',
      width: '90px',
      align: 'center',
      dataCy: 'newly-enrolled-patients-appointment-date',
      sortable: true,
      sortOrder: filter['n-appointmentDate']
        ? tableSortMap[filter['n-appointmentDate']]
        : undefined,
      render(appointmentDate: string) {
        return DateFormatter({
          date: appointmentDate,
          format: DateFormats['MMM d, yyyy'],
        });
      },
    },
    {
      label: i18next.t('labels.type'),
      key: 'type',
      width: '95px',
      align: 'center',
      dataCy: 'newly-enrolled-patients-type',
      sortable: false,
      sortOrder: filter['n-type'] ? tableSortMap[filter['n-type']] : undefined,
    },
    {
      label: i18next.t('labels.callsSinceEnrolled'),
      key: 'calls',
      width: '80px',
      align: 'center',
      dataCy: 'newly-enrolled-patients-calls',
      sortable: true,
      sortOrder: filter['n-calls']
        ? tableSortMap[filter['n-calls']]
        : undefined,
    },
    {
      label: i18next.t('labels.notes'),
      width: '200px',
      key: 'notes',
      render(notes: IKeyNoteData[], record) {
        const note = notes.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        )[0];
        return (
          <NoteCell
            text={note?.note ?? ''}
            onClick={() => {
              setTableNote({
                notes,
                tableName: 'newly',
                patientGuid: record.guid,
                patientName: record.name,
              });
              toggleOpenDrawer();
            }}
          />
        );
      },
      tableCollClassName: s['note-cell'],
      dataCy: 'newly-enrolled-patients-notes',
    },
  ];
};
